import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

interface Event {
  id: string;
  title: string;
  description: string;
  image_url: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
}

export default function EventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', true)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('이벤트 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const isEventOngoing = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return now >= start && now <= end;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              진행중인 이벤트
            </h1>
            <p className="text-xl text-gray-300">
              다양한 이벤트와 특별한 혜택을 만나보세요
            </p>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-slate-800 rounded-full">
                <i className="ri-calendar-event-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                진행중인 이벤트가 없습니다
              </h3>
              <p className="text-gray-400">
                곧 새로운 이벤트가 시작될 예정입니다
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-slate-700/50 hover:border-teal-500/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover object-top"
                    />
                    {isEventOngoing(event.start_date, event.end_date) && (
                      <div className="absolute top-4 right-4 bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                        진행중
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {event.title}
                    </h3>
                    <p className="text-gray-300 mb-4 line-clamp-3">
                      {event.description}
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-calendar-line"></i>
                      </div>
                      <span>
                        {new Date(event.start_date).toLocaleDateString('ko-KR')} ~{' '}
                        {new Date(event.end_date).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
